/**
 * Did a past session actually take place, and who turned up? Shared by the
 * interviewer dashboard, the client portal and the admin console so one booking
 * cannot be described three different ways.
 *
 * The backend resolves this (`attendance_state` / admin `display_status`) and
 * these helpers only translate its verdict into UI wording. The portals used to
 * infer "Completed" from the clock alone (`scheduled_end <= now`), which meant a
 * session nobody attended showed a green Completed badge. That inference is gone.
 *
 * Three states, keyed on who joined the Zoom meeting:
 *
 *   both / a recording exists  → `completed`
 *   exactly one party joined   → `no_show`   (the absentee is `no_show_party`)
 *   neither party joined       → `cancelled` (nobody kept the appointment)
 *
 * An earlier design had a middle state `attendance_unconfirmed` ("Attendance not
 * confirmed"); it is gone, and the rows it covered are `cancelled` now.
 */

export type AttendanceState =
  | 'completed'
  | 'no_show'
  | 'cancelled'
  | null
  | undefined

/** Exactly one party turned up — there is a no-show to report against someone. */
export function isNoShowState(state: AttendanceState): boolean {
  return state === 'no_show'
}

/**
 * Nobody turned up. Not a no-show against either party; grouped with cancelled
 * sessions per the client's rule.
 */
export function isUnattendedState(state: AttendanceState): boolean {
  return state === 'cancelled'
}

/**
 * Who failed to turn up, in words the interviewer reads on their own dashboard.
 * `null` only reaches here on a booking a human marked no-show without naming a
 * party — the derived path always supplies one or resolves to Cancelled.
 */
export function noShowWording(party: string | null | undefined): string {
  if (party === 'client') return "Candidate didn't join"
  if (party === 'interviewer') return 'You missed this session'
  if (party === 'both') return 'No one joined'
  return 'Attendance was not confirmed for this session'
}

/** The reason line on a session that was cancelled because nobody joined. */
export const UNATTENDED_REASON = 'No one joined this session'

/**
 * May the interviewer still write an evaluation? Only for a no-show the CLIENT
 * missed: the interviewer turned up, so their remarks belong on file. Not when
 * the interviewer missed it, and not when nobody joined — that one is reported
 * as Cancelled and carries no evaluation.
 *
 * Mirrors `no_show_allows_evaluation` in scheduling/attendance.py, which is what
 * builds the completable-sessions list. The button would dead-end if these two
 * disagreed, so they take the same two arguments and answer the same way.
 */
export function noShowAllowsEvaluation(
  state: AttendanceState,
  party: string | null | undefined,
): boolean {
  return isNoShowState(state) && party !== 'interviewer' && party !== 'both'
}

/** Why the badge says what it says — tooltip, so nobody has to guess. */
export function attendanceHint(
  state: AttendanceState,
  party?: string | null,
): string | undefined {
  if (isUnattendedState(state)) {
    return (
      'Neither the interviewer nor the candidate is recorded as joining this ' +
      'session, so it is reported as cancelled rather than as a no-show ' +
      'against either of them. An admin can mark it completed if it did go ahead.'
    )
  }
  if (!isNoShowState(state)) return undefined
  if (party === 'client') return 'The candidate did not join this session.'
  if (party === 'interviewer') return 'The interviewer did not join this session.'
  return 'Only one party joined this session.'
}
